import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { abandonedCheckoutsAPI, exportsAPI } from '../services/api';
import Pagination from './Pagination';
import ExportCsvButton from './ExportCsvButton';
import './Users.css';

const initialPerPage = () => {
  const stored = parseInt(localStorage.getItem('per_page') || '', 10);
  return [10, 15, 25, 50, 100].includes(stored) ? stored : 15;
};

const formatMoney = (amount, currency = 'AED') =>
  `${currency} ${Number(amount || 0).toFixed(2)}`;

const AbandonedCheckouts = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: initialPerPage(),
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const prevDebouncedSearch = useRef('');

  const deletableIdsOnPage = useMemo(
    () => rows.filter((row) => row.can_delete !== false).map((row) => row.id),
    [rows],
  );

  const allOnPageSelected =
    deletableIdsOnPage.length > 0 &&
    deletableIdsOnPage.every((id) => selectedIds.includes(id));

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (prevDebouncedSearch.current === debouncedSearch) return;
    prevDebouncedSearch.current = debouncedSearch;
    setPagination((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
      };
      if (statusFilter) params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await abandonedCheckoutsAPI.getAll(params);
      const data = response.data.data || [];
      setRows(data);
      setSelectedIds((prev) => prev.filter((id) => data.some((row) => row.id === id)));
      const pg = response.data.pagination;
      if (pg) {
        setPagination((prev) => ({
          ...prev,
          page: pg.page ?? prev.page,
          per_page: pg.per_page ?? prev.per_page,
          total: pg.total ?? 0,
        }));
      }
    } catch (error) {
      console.error('Error loading abandoned checkouts:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to load abandoned carts');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, statusFilter, debouncedSearch, dateFrom, dateTo]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const handleClearFilters = () => {
    setStatusFilter('');
    setSearchInput('');
    setDebouncedSearch('');
    prevDebouncedSearch.current = '';
    setDateFrom('');
    setDateTo('');
    setSelectedIds([]);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const toggleSelectAllOnPage = () => {
    if (allOnPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !deletableIdsOnPage.includes(id)));
      return;
    }
    setSelectedIds((prev) => [...new Set([...prev, ...deletableIdsOnPage])]);
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleDeleteOne = async (row) => {
    if (!window.confirm('Delete this abandoned cart? This cannot be undone.')) return;

    try {
      await abandonedCheckoutsAPI.delete(row.id);
      toast.success('Abandoned cart deleted.');
      setSelectedIds((prev) => prev.filter((id) => id !== row.id));
      void fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Could not delete cart');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (
      !window.confirm(
        `Delete ${selectedIds.length} selected abandoned cart(s)? This cannot be undone.`,
      )
    ) {
      return;
    }

    setBulkDeleting(true);
    try {
      const response = await abandonedCheckoutsAPI.bulkDelete(selectedIds);
      const errors = response.data.errors || [];
      toast.success(response.data.message || 'Selected carts deleted.');
      if (errors.length) {
        toast.warn(errors.slice(0, 3).join(' '));
      }
      setSelectedIds([]);
      void fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  if (loading && rows.length === 0) {
    return <div className="loading">Loading abandoned carts…</div>;
  }

  return (
    <div className="users-container">
      <div className="search-filter-bar">
        <div className="filter-field">
          <label htmlFor="abandoned-status">Checkout status</label>
          <select
            id="abandoned-status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          >
            <option value="">Incomplete (default)</option>
            <option value="active">Active — awaiting payment</option>
            <option value="expired">Expired</option>
            <option value="failed">Payment failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div className="filter-field filter-field--grow">
          <label htmlFor="abandoned-search">Buyer or product</label>
          <input
            id="abandoned-search"
            type="search"
            placeholder="Search email, name, product, or gateway ref…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="filter-field">
          <label htmlFor="abandoned-from">From date</label>
          <input
            id="abandoned-from"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          />
        </div>

        <div className="filter-field">
          <label htmlFor="abandoned-to">To date</label>
          <input
            id="abandoned-to"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          />
        </div>

        <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
          Clear filters
        </button>
        <ExportCsvButton
          exportRequest={exportsAPI.abandonedCheckouts}
          filters={{
            status: statusFilter || undefined,
            search: debouncedSearch || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
          }}
          filename="abandoned-checkouts.csv"
        />
      </div>

      {selectedIds.length > 0 ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
            padding: '10px 14px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
          }}
        >
          <span style={{ fontSize: '14px' }}>{selectedIds.length} selected</span>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            disabled={bulkDeleting}
            onClick={() => void handleBulkDelete()}
          >
            {bulkDeleting ? 'Deleting…' : 'Delete selected'}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setSelectedIds([])}
          >
            Clear selection
          </button>
        </div>
      ) : null}

      {loading && rows.length > 0 ? (
        <div className="list-loading-hint">Updating…</div>
      ) : null}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  disabled={!deletableIdsOnPage.length}
                  onChange={toggleSelectAllOnPage}
                  aria-label="Select all deletable carts on this page"
                />
              </th>
              <th>Started</th>
              <th>Buyer</th>
              <th>Items</th>
              <th>Total</th>
              <th>Payment</th>
              <th>Reason</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="8" className="table-empty">
                  No abandoned carts found
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const canDelete = row.can_delete !== false;
                return (
                  <tr key={row.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        disabled={!canDelete}
                        onChange={() => toggleSelectRow(row.id)}
                        aria-label={`Select cart ${row.id}`}
                      />
                    </td>
                    <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                    <td>
                      <div>{row.buyer_label || 'Guest'}</div>
                      {row.guest_email ? (
                        <small style={{ color: '#6b5862' }}>{row.guest_email}</small>
                      ) : null}
                    </td>
                    <td>{row.item_count || 0}</td>
                    <td>{formatMoney(row.total_price, row.currency)}</td>
                    <td>
                      {row.payment_status}
                      {row.is_expired && row.payment_status === 'pending' ? ' (expired)' : ''}
                    </td>
                    <td style={{ maxWidth: 220 }}>
                      {row.payment_failure_reason || '—'}
                    </td>
                    <td className="actions" style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate(`/abandoned-checkouts/${row.id}`)}
                      >
                        Details
                      </button>
                      {canDelete ? (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          style={{ marginLeft: '6px' }}
                          onClick={() => void handleDeleteOne(row)}
                        >
                          Delete
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination.total > pagination.per_page && (
        <Pagination
          pagination={pagination}
          onPageChange={(page) => setPagination((p) => ({ ...p, page }))}
          onPerPageChange={(per_page) => setPagination((p) => ({ ...p, per_page, page: 1 }))}
        />
      )}
    </div>
  );
};

export default AbandonedCheckouts;
